/**
 * <gallery-loading>
 * Preloads a gallery's next images and shows the loading state over the
 * current ones until they arrive. See docs/WEB_COMPONENTS.md.
 */

// Cached swaps settle before the overlay shows, so they look instant
const SHOW_DELAY_MS = 150;
// Once shown, the overlay stays at least this long so it doesn't flicker
const MIN_SHOWN_MS = 300;
const TIMEOUT_MS = 8000;

const SUPERSEDED = Symbol("superseded");
const TIMED_OUT = Symbol("timed out");

function preload(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(true));
    image.addEventListener("error", () => resolve(false));
    image.src = url;
  });
}

// The first candidate that loads, or null if they all error
async function preloadLayer(candidates) {
  for (const url of candidates) {
    if (await preload(url)) return url;
  }
  return null;
}

class GalleryLoading extends HTMLElement {
  #cancelPending = null;
  #showTimer = null;
  #minShownTimer = null;
  // Resolves once the overlay has been up for MIN_SHOWN_MS
  #minShown = null;

  /**
   * Takes one list of candidate URLs per layer (e.g. webp then png).
   * Resolves to { outcome, urls } once every layer has settled, where urls
   * holds the URL that loaded per layer (null if none did) and outcome is
   * "loaded", "missing" (a layer had no URL load) or "timeout". Resolves
   * null if a newer load() superseded this one.
   */
  async load(candidatesPerLayer) {
    this.#cancelPending?.();
    const superseded = new Promise((resolve) => {
      this.#cancelPending = () => resolve(SUPERSEDED);
    });

    this.#start();

    let timeout;
    const urls = await Promise.race([
      Promise.all(candidatesPerLayer.map(preloadLayer)),
      new Promise((resolve) => {
        timeout = setTimeout(() => resolve(TIMED_OUT), TIMEOUT_MS);
      }),
      superseded,
    ]);
    clearTimeout(timeout);
    if (urls === SUPERSEDED) return null;

    if (this.hasAttribute("loading")) {
      if ((await Promise.race([this.#minShown, superseded])) === SUPERSEDED) {
        return null;
      }
    }

    this.#cancelPending = null;
    this.#end();

    if (urls === TIMED_OUT) {
      return { outcome: "timeout", urls: candidatesPerLayer.map(() => null) };
    }
    const outcome = urls.includes(null) ? "missing" : "loaded";
    return { outcome, urls };
  }

  /**
   * Supersedes the load in flight (it resolves null) and clears the loading
   * state, for when the gallery no longer needs a new image.
   */
  cancel() {
    if (!this.#cancelPending) return;

    this.#cancelPending();
    this.#cancelPending = null;
    this.#end();
  }

  // One loading state spans a run of superseding requests
  #start() {
    if (this.hasAttribute("aria-busy")) return;

    this.setAttribute("aria-busy", "true");
    this.#showTimer = setTimeout(
      () => this.setAttribute("loading", ""),
      SHOW_DELAY_MS
    );
    this.#minShown = new Promise((resolve) => {
      this.#minShownTimer = setTimeout(resolve, SHOW_DELAY_MS + MIN_SHOWN_MS);
    });
    this.dispatchEvent(
      new CustomEvent("gallery:loading:start", { bubbles: true })
    );
  }

  #end() {
    clearTimeout(this.#showTimer);
    clearTimeout(this.#minShownTimer);
    this.removeAttribute("loading");
    this.removeAttribute("aria-busy");
    this.dispatchEvent(
      new CustomEvent("gallery:loading:end", { bubbles: true })
    );
  }
}

customElements.define("gallery-loading", GalleryLoading);
